import React from 'react';
import styled from 'styled-components';
import { rem } from 'polished';
import { Table, Td, Th, Tr, Thead, Tbody } from 'oxyrion-ui/lib/Table';

const StringValue = styled.div``;

const ProductLink = styled.a`
  font-size: 12px;
  color: #70ae04;
  font-weight: bold;
  margin-bottom: ${rem(4)};
  text-decoration: none;
`;

const normalizeUnit = (unit, selectedUnit) => {
  if (Array.isArray(unit)) {
    if (unit.find(u => u.value === selectedUnit)) {
      return unit.find(u => u.value === selectedUnit).label;
    }
    return unit && unit[0] ? unit[0].label : '';
  }

  if (unit === '-') {
    return '';
  }

  return unit || '';
};

const normalizeTableData = data => {
  const removedNotUsedRows = data
    .filter(
      d => d.columns.filter(c => c.value && c.value.length > 0).length > 0,
    )
    .map(d =>
      Object.assign(d, {
        columns: d.columns.filter(c => c.value && c.value.length > 0),
      }),
    );

  return removedNotUsedRows;
};

export const translate = (
  translations,
  lang,
  defaultValue,
  field = 'value',
) => {
  if (!translations) {
    return defaultValue;
  }

  if (translations.find(t => t.lang === lang)) {
    return translations.find(t => t.lang === lang)[field];
  }

  return defaultValue;
};

export const normalizeParam = (fullParam, lang = 'SK') => {
  if (fullParam.type === 'string' || fullParam.type === 'number') {
    return {
      label: translate(fullParam.translations, lang, fullParam.name),
      group:
        fullParam.group && fullParam.group.length > 0
          ? translate(fullParam.group_translations, lang, fullParam.group)
          : 'not-grouped',
      value: (
        <StringValue>{`${fullParam.value} ${normalizeUnit(
          fullParam.unit,
          fullParam.selectedUnit,
        )}`}</StringValue>
      ),
    };
  }

  if (fullParam.type === 'selectGroup') {
    let value;
    if (Array.isArray(fullParam.value)) {
      value = fullParam.value
        .map(f => translate(f.translations, lang, f.label, 'label'))
        .join(', ');
    } else {
      value =
        fullParam.value &&
        translate(
          fullParam.value.translations,
          lang,
          fullParam.value.label || fullParam.value.value,
          'label',
        );
    }
    return {
      label: translate(fullParam.translations, lang, fullParam.name),
      isTable: true,
      group:
        fullParam.group && fullParam.group.length > 0
          ? translate(fullParam.group_translations, lang, fullParam.group)
          : 'not-grouped',
      value: (
        <StringValue>{`${value} ${normalizeUnit(
          fullParam.unit,
          fullParam.selectedUnit,
        )}`}</StringValue>
      ),
    };
  }

  if (fullParam.type === 'multiSelect') {
    let value;
    if (Array.isArray(fullParam.value)) {
      value = fullParam.value
        .map(f => translate(f.translations, lang, f.label, 'label'))
        .join(', ');
    } else {
      value =
        fullParam.value &&
        translate(
          fullParam.value.translations,
          lang,
          fullParam.value.label || fullParam.value.value,
          'label',
        );
    }
    return {
      label: translate(fullParam.translations, lang, fullParam.name),
      group:
        fullParam.group && fullParam.group.length > 0
          ? translate(fullParam.group_translations, lang, fullParam.group)
          : 'not-grouped',
      value: (
        <StringValue>{`${value} ${normalizeUnit(
          fullParam.unit,
          fullParam.selectedUnit,
        )}`}</StringValue>
      ),
    };
  }

  if (fullParam.type === 'securityCertificateSelector') {
    let value;
    if (Array.isArray(fullParam.value)) {
      value = fullParam.value
        .map(f => translate(f.translations, lang, f.label, 'label'))
        .join(', ');
    } else {
      value =
        fullParam.value &&
        translate(
          fullParam.value.translations,
          lang,
          fullParam.value.label || fullParam.value.value,
          'label',
        );
    }
    return {
      label: translate(fullParam.translations, lang, fullParam.name),
      group:
        fullParam.group && fullParam.group.length > 0
          ? translate(fullParam.group_translations, lang, fullParam.group)
          : 'not-grouped',
      value: (
        <StringValue>{`${value} ${normalizeUnit(
          fullParam.unit,
          fullParam.selectedUnit,
        )}`}</StringValue>
      ),
    };
  }

  if (fullParam.type === 'codelist') {
    return {
      label: translate(fullParam.translations, lang, fullParam.name),
      group:
        fullParam.group && fullParam.group.length > 0
          ? translate(fullParam.group_translations, lang, fullParam.group)
          : 'not-grouped',
      value: (
        <StringValue>{`${
          fullParam.value
            ? translate(
                fullParam.value.translations,
                lang,
                fullParam.value.label,
                'label',
              )
            : ''
        } ${normalizeUnit(
          fullParam.unit,
          fullParam.selectedUnit,
        )}`}</StringValue>
      ),
    };
  }

  if (fullParam.type === 'table') {
    const f = fullParam;
    const tableValues = normalizeTableData(f.value);

    return {
      label: translate(fullParam.translations, lang, fullParam.name),
      group:
        fullParam.group && fullParam.group.length > 0
          ? translate(fullParam.group_translations, lang, fullParam.group)
          : 'not-grouped',
      isTable: true,
      value: tableValues.length ? (
        <Table>
          {tableValues[0] && (
            <Thead>
              <Th>{tableValues[0].name}</Th>
              {tableValues[0].columns.map(col => (
                <Th>{col.value || '-'}</Th>
              ))}
              <Th>{tableValues[0].unit}</Th>
            </Thead>
          )}
          <Tbody>
            {tableValues.slice(1, tableValues.length).map(v => (
              <Tr>
                <Td>{v.name}</Td>
                {v.columns.map(col => (
                  <Td>{col.value || '-'}</Td>
                ))}
                <Th>{v.unit}</Th>
              </Tr>
            ))}
          </Tbody>
        </Table>
      ) : (
        undefined
      ),
    };
  }

  if (fullParam.type === 'catalogProductsSelector') {
    let value;

    if (Array.isArray(fullParam.value)) {
      value = fullParam.value.map(v => (
        <StringValue style={{ display: 'flex' }}>
          <ProductLink target="_blank" href={`./${v.value}`}>
            {v.label}
          </ProductLink>
        </StringValue>
      ));
    } else {
      value = (
        <StringValue>
          <ProductLink target="_blank" href={`./${fullParam.value.value}`}>
            {fullParam.value.label}
          </ProductLink>
        </StringValue>
      );
    }
    return {
      label: translate(fullParam.translations, lang, fullParam.name),
      group:
        fullParam.group && fullParam.group.length > 0
          ? translate(fullParam.group_translations, lang, fullParam.group)
          : 'not-grouped',
      isTable: value.length > 0,
      value,
    };
  }

  return {
    label: translate(fullParam.translations, lang, fullParam.name),
    group:
      fullParam.group && fullParam.group.length > 0
        ? translate(fullParam.group_translations, lang, fullParam.group)
        : 'not-grouped',
    value: fullParam.value,
  };

  // return fullParam;
};

export const normalizeParams = (params, lang) => {
  const categorized = [];

  params
    .filter(
      p =>
        !p.hide &&
        p.value &&
        (p.value.length || p.value.value) &&
        (!Array.isArray(p.value) ||
          (Array.isArray(p.value) && p.value.filter(v => v).length > 0)),
    )
    .forEach(p => {
      const normalized = normalizeParam(p, lang);

      if (!categorized.find(c => c.group === normalized.group)) {
        categorized.push({
          group: normalized.group,
          params: [],
        });
      }
      if (normalized.value) {
        categorized
          .find(c => c.group === normalized.group)
          .params.push({
            value: normalized.value,
            label: normalized.label,
            isTable: normalized.isTable,
          });
      }
    });

  return categorized;
};



// WEBPACK FOOTER //
// ./src/Utils/CatalogProductParamsNormalizer/index.jsx